import { UISchemaElement } from '@jsonforms/core';
import CT from '@marketplace-ai/tags';
import { FormProvider } from 'react-hook-form';
import { ActorRefFrom } from 'xstate';
import { Button, FormActions, FormLayout } from '~/components';
import { ajvResolver } from '~/components/JSONForm/ajvResolver';
import { JSONFormAdapter } from '~/components/JSONForm/JsonFormAdapter';
import { useCustomForm } from '~/hooks/useCustomForm';
import { JSONFormData } from '~/types';
import { attestationMachine } from './attestation.machine';

const TAGS = CT.attestations;
const GENERIC_TAGS = CT.genericJSONForm;

interface WrapperProps {
  defaultValues: JSONFormData;
  formDataSchema: object;
  layoutSchema: UISchemaElement;
  actor: ActorRefFrom<typeof attestationMachine>;
}

export const JSONFormWrapper = (props: WrapperProps) => {
  const { actor, defaultValues, formDataSchema, layoutSchema } = props;
  const methods = useCustomForm({
    resolver: ajvResolver(formDataSchema),
    defaultValues: props.defaultValues || {},
  });

  // Validation errors display inline on fields — no toast needed
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const onSubmitFailure = () => {};

  const onSubmitSuccess = () => {
    actor.send({ type: 'ON_SUBMIT', values: methods.watch() as JSONFormData });
  };

  const handleSave = () => {
    actor.send({ type: 'ON_SAVE', values: methods.watch() as JSONFormData });
  };

  return (
    <FormProvider {...methods}>
      {/* [MCV-1284, MCV-1577] The form used to cap itself at `max-h-[75vh]` and
          run its own `flex-1 min-h-0 overflow-y-auto` body so the dialog's
          scroll never engaged. FormDialog now owns both: it caps the panel
          (`max-h-[95vh] sm:max-h-[90vh]`), is itself a flex column, and scrolls
          its body (`flex-1 min-h-0 overflow-y-auto`). Keeping the second cap
          here stopped this questionnaire ~15vh short of the height the dialog
          allows on tall screens, so the form is now a plain FormLayout and the
          sticky FormActions pins to the dialog's scroll container -- the same
          shell the standardized PeerReqForm renders in. */}
      <form
        onSubmit={methods.handleSubmit(onSubmitSuccess, onSubmitFailure)}
        data-cy={TAGS.form.form}
      >
        <FormLayout>
          <JSONFormAdapter
            formDataSchema={formDataSchema}
            layoutSchema={layoutSchema}
            defaultValues={defaultValues}
          />
          <FormActions>
            <Button
              cyTag={TAGS.form.form + GENERIC_TAGS.saveBtn}
              onClick={handleSave}
              type="button"
              colorVariant="secondary"
            >
              Save Progress
            </Button>
            <Button cyTag={TAGS.form.form + GENERIC_TAGS.submitBtn} type="submit">
              Submit
            </Button>
          </FormActions>
        </FormLayout>
      </form>
    </FormProvider>
  );
};
